import {
  IsString,
  IsNumber,
  IsIn,
  IsBoolean,
  IsOptional,
  IsDefined,
  ValidateNested,
  Min,
  Max,
} from 'class-validator';
import { Type, Transform } from 'class-transformer';
import { IsColorOrReference } from '@builder4/shared/dto/styles/validators/IsColorOrReference';
import { IsRgbaOrReference } from '@builder4/shared/dto/styles/validators/IsRgbaOrReference';
import {
  VisibilityStateDto,
  PositionAnchorsStateDto,
} from '@builder4/shared/dto/craft/props/shared';
import { IsTRBLNumberCoords } from '@builder4/shared/dto/craft/validators/IsTRBLNumberCoords';
import { IsTextStyles } from '@builder4/shared/dto/craft/validators/IsTextStyles';
import { GradientDto, RgbaDto } from '@builder4/shared/dto/styles';
import type { Descendant } from 'slate';
import {
  ANIMATION_TYPES,
  ANIMATION_EASINGS,
  HOVER_EFFECT_TYPES,
} from '@builder4/shared/types/animation';
import { TYPOGRAPHY_STYLE_VARIANT_VALUES } from '@builder4/shared/styles/typographyVariants';

const TEXT_ALIGN_VALUES = ['left', 'right', 'center', 'justify', 'start', 'end'] as const;
const POSITION_VALUES = ['relative', 'absolute', 'fixed', 'sticky'] as const;
const FILL_SPACE_VALUES = ['yes', 'no'] as const;
const MAX_WIDTH_UNIT_VALUES = ['px', '%'] as const;
const STYLE_VARIANT_VALUES = TYPOGRAPHY_STYLE_VARIANT_VALUES;

/**
 * Breakpoint-aware properties for Text element.
 * Derived from getDetachablePropsForNode('Text'):
 * - DETACHABLE_PROPS_REGISTRY: width, flexBasis, fillSpace
 * - POSITION_PROPS: position, positionAnchorsState, left, right, top, bottom, zIndex
 * - BOUNDING_BOX_PROPS: padding, margin, borderRadius
 * - MAX_WIDTH_PROPS: maxWidth, maxWidthUnit
 */
export class TextBreakpointPropsDto {
  // From DETACHABLE_PROPS_REGISTRY[TEXT_NODE_NAME]
  @IsOptional()
  @IsString()
  width?: string;

  @IsOptional()
  @IsIn([...FILL_SPACE_VALUES])
  fillSpace?: (typeof FILL_SPACE_VALUES)[number];

  @IsOptional()
  @IsString()
  flexBasis?: string;

  // From BOUNDING_BOX_PROPS
  @IsOptional()
  @IsTRBLNumberCoords()
  padding?: [number, number, number, number];

  @IsOptional()
  @IsTRBLNumberCoords()
  margin?: [number, number, number, number];

  @IsOptional()
  @IsTRBLNumberCoords()
  borderRadius?: [number, number, number, number];

  // From POSITION_PROPS
  @IsOptional()
  @IsIn([...POSITION_VALUES])
  position?: (typeof POSITION_VALUES)[number];

  @IsOptional()
  @ValidateNested()
  @Type(() => PositionAnchorsStateDto)
  positionAnchorsState?: PositionAnchorsStateDto;

  @IsOptional()
  @IsString()
  left?: string;

  @IsOptional()
  @IsString()
  right?: string;

  @IsOptional()
  @IsString()
  top?: string;

  @IsOptional()
  @IsString()
  bottom?: string;

  @IsOptional()
  @IsNumber()
  @Min(-1)
  @Max(100)
  zIndex?: number;

  // From MAX_WIDTH_PROPS
  @IsOptional()
  @IsNumber()
  @Min(0)
  maxWidth?: number;

  @IsOptional()
  @IsIn([...MAX_WIDTH_UNIT_VALUES])
  maxWidthUnit?: (typeof MAX_WIDTH_UNIT_VALUES)[number];
}

export class TextPropsDto {
  // Sizing
  @IsDefined()
  @IsNumber()
  @Min(0)
  maxWidth!: number;

  @IsOptional()
  @IsIn([...MAX_WIDTH_UNIT_VALUES])
  maxWidthUnit?: (typeof MAX_WIDTH_UNIT_VALUES)[number];

  @IsDefined()
  @IsString()
  width!: string;

  @IsOptional()
  @IsString()
  height?: string;

  @IsDefined()
  @IsIn([...FILL_SPACE_VALUES])
  fillSpace!: (typeof FILL_SPACE_VALUES)[number];

  // Typography
  @IsDefined()
  @IsIn([...TEXT_ALIGN_VALUES])
  textAlign!: (typeof TEXT_ALIGN_VALUES)[number];

  @IsDefined()
  @IsNumber()
  lineHeight!: number;

  // Background
  @IsOptional()
  @IsColorOrReference()
  backgroundColor?: RgbaDto | GradientDto | string;

  @IsOptional()
  @ValidateNested()
  @Type(() => GradientDto)
  bgGradient?: GradientDto;

  @IsOptional()
  @IsRgbaOrReference()
  bgSolid?: RgbaDto | string;

  // Spacing
  @IsDefined()
  @IsTRBLNumberCoords()
  padding!: [number, number, number, number];

  @IsDefined()
  @IsTRBLNumberCoords()
  margin!: [number, number, number, number];

  @IsDefined()
  @IsTRBLNumberCoords()
  borderRadius!: [number, number, number, number];

  // Shadow
  @IsDefined()
  @IsNumber()
  @Min(0)
  shadow!: number;

  // Position
  @IsDefined()
  @IsIn([...POSITION_VALUES])
  position!: (typeof POSITION_VALUES)[number];

  @IsOptional()
  @IsString()
  top?: string;

  @IsOptional()
  @IsString()
  left?: string;

  @IsOptional()
  @IsString()
  right?: string;

  @IsOptional()
  @IsString()
  bottom?: string;

  @IsDefined()
  @ValidateNested()
  @Type(() => PositionAnchorsStateDto)
  positionAnchorsState!: PositionAnchorsStateDto;

  @IsDefined()
  @IsNumber()
  @Min(-1)
  @Max(100)
  zIndex!: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  borderWidth?: number;

  // Visibility
  @IsDefined()
  @ValidateNested()
  @Type(() => VisibilityStateDto)
  visibility!: VisibilityStateDto;

  // Text content
  @IsDefined()
  @IsBoolean()
  appliedGlobalStyle!: boolean;

  // WHY: class-transformer with enableImplicitConversion:true sees the Array runtime type and
  // tries to coerce each Descendant element, mangling the deeply nested Slate node structure.
  // Returning obj.textStyles (the raw source value) bypasses that coercion entirely so that
  // @IsTextStyles() receives the original, unmodified Slate tree.
  @Transform(({ obj }) => obj.textStyles)
  @IsDefined()
  @IsTextStyles()
  textStyles!: Descendant[];

  // Style variant (typography global style reference)
  @IsOptional()
  @IsIn([...STYLE_VARIANT_VALUES])
  selectedStyleVariant?: (typeof STYLE_VARIANT_VALUES)[number] | null;

  // Flex
  @IsOptional()
  @IsString()
  flexBasis?: string;

  // Animation
  @IsOptional()
  @IsIn([...ANIMATION_TYPES])
  animationType?: (typeof ANIMATION_TYPES)[number];

  @IsOptional()
  @IsNumber()
  @Min(100)
  @Max(2000)
  animationDuration?: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(2000)
  animationDelay?: number;

  @IsOptional()
  @IsIn([...ANIMATION_EASINGS])
  animationEasing?: (typeof ANIMATION_EASINGS)[number];

  // Hover effect
  @IsOptional()
  @IsIn([...HOVER_EFFECT_TYPES])
  hoverEffect?: (typeof HOVER_EFFECT_TYPES)[number];

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(2000)
  hoverTransitionDuration?: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(200)
  hoverIntensity?: number;

  // Breakpoint overrides
  @IsOptional()
  @ValidateNested()
  @Type(() => TextBreakpointPropsDto)
  tablet?: TextBreakpointPropsDto;

  @IsOptional()
  @ValidateNested()
  @Type(() => TextBreakpointPropsDto)
  mobile?: TextBreakpointPropsDto;
}
